import { calendarDateRange, parseCalendarDate } from "./calendar-date";
import {
  isCalendarRangeWithinMonths,
  MAX_FLEXIBLE_DATE_RANGE_MONTHS
} from "../../../work-type-shared/calendar-date-range";
import type {
  WorkPolicyApplicationDraft,
  WorkPolicyApplicationScheduleRow
} from "./work-policy-application.types";

export interface WorkPolicyApplicationValidationIssue {
  path: string;
  message: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function issue(
  issues: WorkPolicyApplicationValidationIssue[],
  path: string,
  message: string
) {
  issues.push({ path, message });
}

function isClock(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
  return Boolean(
    match &&
      Number(match[1]) <= 23 &&
      Number(match[2]) <= 59 &&
      (match[3] === undefined || Number(match[3]) <= 59)
  );
}

function validDate(
  value: unknown,
  path: string,
  issues: WorkPolicyApplicationValidationIssue[]
): value is string {
  if (typeof value !== "string") {
    issue(issues, path, "Must be a YYYY-MM-DD calendar date.");
    return false;
  }
  try {
    parseCalendarDate(value);
    return true;
  } catch {
    issue(issues, path, "Must be a valid YYYY-MM-DD calendar date.");
    return false;
  }
}

function validateScheduleRow(
  value: unknown,
  index: number,
  mode: "DAILY_DATE" | "WEEKLY_PATTERN",
  startDate: string,
  endDate: string,
  issues: WorkPolicyApplicationValidationIssue[]
): WorkPolicyApplicationScheduleRow | null {
  const path = `schedule[${index}]`;
  if (!isRecord(value)) {
    issue(issues, path, "Must be an object.");
    return null;
  }
  const workday = value.workday === true;
  if (typeof value.workday !== "boolean") issue(issues, `${path}.workday`, "Must be a boolean.");
  if (!Number.isInteger(value.plannedWorkMinutes) || Number(value.plannedWorkMinutes) < 0) {
    issue(issues, `${path}.plannedWorkMinutes`, "Must be a non-negative integer.");
  }
  if (typeof value.breakWaiver !== "boolean") {
    issue(issues, `${path}.breakWaiver`, "Must be a boolean.");
  }
  const plannedWorkMinutes = Number.isInteger(value.plannedWorkMinutes)
    ? Number(value.plannedWorkMinutes)
    : 0;
  const breakWaiver = value.breakWaiver === true;
  const startTime = value.startTime;
  if (startTime !== null && !isClock(startTime)) {
    issue(issues, `${path}.startTime`, "Must be null or a valid clock time.");
  }
  if (!workday && (startTime !== null || plannedWorkMinutes !== 0 || breakWaiver)) {
    issue(issues, path, "A non-workday cannot contain work detail.");
  }
  if (workday && (!isClock(startTime) || plannedWorkMinutes <= 0)) {
    issue(issues, path, "A workday requires a start time and positive minutes.");
  }
  if (breakWaiver && plannedWorkMinutes !== 240) {
    issue(issues, `${path}.breakWaiver`, "Only an exact four-hour row may waive break.");
  }

  let workDate: string | null = null;
  let weekdayNo: number | null = null;
  if (mode === "DAILY_DATE") {
    if (validDate(value.workDate, `${path}.workDate`, issues)) {
      workDate = value.workDate;
      if (workDate < startDate || workDate > endDate) {
        issue(issues, `${path}.workDate`, "Must be inside the application range.");
      }
    }
    if (value.weekdayNo !== null) issue(issues, `${path}.weekdayNo`, "Must be null for DAILY_DATE.");
  } else {
    if (!Number.isInteger(value.weekdayNo) || Number(value.weekdayNo) < 1 || Number(value.weekdayNo) > 7) {
      issue(issues, `${path}.weekdayNo`, "Must be from 1 through 7.");
    } else {
      weekdayNo = Number(value.weekdayNo);
    }
    if (value.workDate !== null) issue(issues, `${path}.workDate`, "Must be null for WEEKLY_PATTERN.");
  }

  return {
    workDate,
    weekdayNo,
    startTime: isClock(startTime) ? startTime : null,
    plannedWorkMinutes,
    breakWaiver,
    workday
  };
}

export function validateWorkPolicyApplicationDraft(
  value: unknown,
  options: { requireSelectiveSettlementReady?: boolean } = {}
): WorkPolicyApplicationValidationIssue[] {
  const issues: WorkPolicyApplicationValidationIssue[] = [];
  if (!isRecord(value)) return [{ path: "draft", message: "Must be an object." }];
  if (!Number.isInteger(value.workPolicyId) || Number(value.workPolicyId) < 1) {
    issue(issues, "workPolicyId", "Must be a positive integer.");
  }
  const startValid = validDate(value.startDate, "startDate", issues);
  const endValid = validDate(value.endDate, "endDate", issues);
  const startDate = startValid ? String(value.startDate) : "0001-01-01";
  const endDate = endValid ? String(value.endDate) : "0001-01-01";
  if (startValid && endValid && startDate > endDate) {
    issue(issues, "endDate", "Must be on or after startDate.");
  }

  if (value.workType === "staggered") {
    if (!isClock(value.selectedStartTime)) {
      issue(issues, "selectedStartTime", "Must be a valid selected start time.");
    } else if (
      !Array.isArray(value.allowedStartTimes) ||
      !value.allowedStartTimes.every(isClock) ||
      !value.allowedStartTimes.includes(value.selectedStartTime)
    ) {
      issue(issues, "selectedStartTime", "Must be one of the server allowed start times.");
    }
    return issues;
  }
  if (value.workType === "selective") {
    if (
      options.requireSelectiveSettlementReady &&
      value.settlementStatus !== "ready"
    ) {
      issue(
        issues,
        "settlementPeriods",
        "정산 구간 계산을 완료한 뒤 상신해 주세요."
      );
    }
    if (!Array.isArray(value.settlementPeriods)) {
      issue(issues, "settlementPeriods", "Must be an array.");
    }
    return issues;
  }
  if (value.workType !== "flexible") {
    issue(issues, "workType", "Must be staggered, flexible, or selective.");
    return issues;
  }
  if (value.flexibleMode !== "DAILY_DATE" && value.flexibleMode !== "WEEKLY_PATTERN") {
    issue(issues, "flexibleMode", "Must be DAILY_DATE or WEEKLY_PATTERN.");
    return issues;
  }
  const dailyRangeWithinLimit =
    value.flexibleMode !== "DAILY_DATE" ||
    !startValid ||
    !endValid ||
    startDate > endDate ||
    isCalendarRangeWithinMonths(
      startDate,
      endDate,
      MAX_FLEXIBLE_DATE_RANGE_MONTHS
    );
  if (!dailyRangeWithinLimit) {
    issue(issues, "endDate", "Must be within 6 calendar months of startDate.");
  }
  if (!Array.isArray(value.schedule) || value.schedule.length === 0) {
    issue(issues, "schedule", "Must not be empty.");
    return issues;
  }
  const mode = value.flexibleMode;
  const rows = value.schedule
    .map((row, index) => validateScheduleRow(row, index, mode, startDate, endDate, issues))
    .filter((row): row is WorkPolicyApplicationScheduleRow => row !== null);
  const identities = rows.map(row =>
    mode === "DAILY_DATE" ? row.workDate : row.weekdayNo
  );
  if (new Set(identities).size !== identities.length) {
    issue(issues, "schedule", "Rows must be unique.");
  }
  if (
    startValid &&
    endValid &&
    startDate <= endDate &&
    dailyRangeWithinLimit
  ) {
    if (mode === "DAILY_DATE") {
      const expected = calendarDateRange(startDate, endDate);
      const actual = rows
        .map(row => row.workDate)
        .filter((date): date is string => date !== null)
        .sort();
      if (expected.length !== actual.length || expected.some((date, index) => date !== actual[index])) {
        issue(issues, "schedule", "DAILY_DATE requires one row for every application date.");
      }
    } else if (
      rows.length !== 7 ||
      ![1, 2, 3, 4, 5, 6, 7].every(day => identities.includes(day))
    ) {
      issue(issues, "schedule", "WEEKLY_PATTERN requires weekday rows 1 through 7.");
    }
  }
  return issues;
}

export function assertValidWorkPolicyApplicationDraft(
  value: unknown
): asserts value is WorkPolicyApplicationDraft {
  const issues = validateWorkPolicyApplicationDraft(value);
  if (issues.length > 0) {
    const error = new Error("The work-policy application draft is invalid.");
    Object.assign(error, { code: "WORK_POLICY_VALIDATION", issues });
    throw error;
  }
}
